import { RestartAlt, Settings, ZoomIn, ZoomOut } from '@mui/icons-material';
import { IconButton, Tooltip, Typography } from '@mui/material';
import { Timespan } from '@thingslog/repositories';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { TimeframeChip } from './TimeframeChip';

export interface WidgetHeaderZoomControls {
  canZoomIn: boolean;
  canZoomOut: boolean;
  isZoomed: boolean;
  onReset: () => void;
  onZoomIn: () => void;
  onZoomOut: () => void;
}

interface Props {
  title: string;
  onSettings: () => void;
  timeframe?: Timespan | null;
  isTimeframeInheritedFromDashboard?: boolean;
  onTimeframeClick?: () => void;
  showScrollZoomHint?: boolean;
  zoomControls?: WidgetHeaderZoomControls | null;
}

export const WidgetHeader: React.FC<Props> = ({
  title,
  onSettings,
  timeframe,
  isTimeframeInheritedFromDashboard,
  onTimeframeClick,
  showScrollZoomHint = false,
  zoomControls = null,
}: Props) => {
  const { t } = useTranslation();

  return (
    <div className="flex min-w-0 flex-row items-center justify-between gap-2">
      <Typography
        variant="h6"
        fontWeight="bold"
        component="span"
        className="min-w-0 flex-1 truncate text-left"
      >
        {title || t('dashboard_widget', 'Widget')}
      </Typography>
      <div className="flex min-w-0 shrink-0 items-center gap-1">
        {showScrollZoomHint && (
          <Typography
            variant="caption"
            color="text.secondary"
            component="span"
            className="max-w-[6.5rem] text-xs leading-tight sm:max-w-none sm:whitespace-nowrap"
          >
            {t('dashboard_shift_scroll_to_zoom', 'Shift + scroll to zoom')}
          </Typography>
        )}
        {zoomControls && (
          <div className="flex shrink-0 items-center">
            <Tooltip title={t('dashboard_zoom_in', 'Zoom in')}>
              <span>
                <IconButton
                  aria-label={t('dashboard_zoom_in', 'Zoom in')}
                  color="inherit"
                  size="small"
                  className="shrink-0 text-gray-500"
                  disabled={!zoomControls.canZoomIn}
                  onClick={zoomControls.onZoomIn}
                >
                  <ZoomIn fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title={t('dashboard_zoom_out', 'Zoom out')}>
              <span>
                <IconButton
                  aria-label={t('dashboard_zoom_out', 'Zoom out')}
                  color="inherit"
                  size="small"
                  className="shrink-0 text-gray-500"
                  disabled={!zoomControls.canZoomOut}
                  onClick={zoomControls.onZoomOut}
                >
                  <ZoomOut fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title={t('dashboard_zoom_reset', 'Reset zoom')}>
              <span>
                <IconButton
                  aria-label={t('dashboard_zoom_reset', 'Reset zoom')}
                  color="inherit"
                  size="small"
                  className="shrink-0 text-gray-500"
                  disabled={!zoomControls.isZoomed}
                  onClick={zoomControls.onReset}
                >
                  <RestartAlt fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </div>
        )}
        {timeframe && onTimeframeClick && (
          <TimeframeChip
            timespan={timeframe}
            title={
              isTimeframeInheritedFromDashboard
                ? t(
                    'dashboard_edit_widget_timeframe_inherited',
                    'Edit widget timeframe. Currently inherited from dashboard.'
                  )
                : t('dashboard_edit_widget_timeframe', 'Edit widget timeframe')
            }
            isInheritedFromDashboard={isTimeframeInheritedFromDashboard}
            onClick={onTimeframeClick}
            className="max-w-[8rem] sm:max-w-[13rem]"
          />
        )}
        <Tooltip title={t('dashboard_widget_settings', 'Widget settings')}>
          <IconButton
            aria-label={t('dashboard_widget_settings', 'Widget settings')}
            color="inherit"
            size="small"
            className="shrink-0 text-gray-500"
            onClick={onSettings}
          >
            <Settings fontSize="small" />
          </IconButton>
        </Tooltip>
      </div>
    </div>
  );
};
